import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { isEmpty } from 'lodash-es';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, switchMap, tap, mergeMap } from 'rxjs/operators';

import { GatewayApi, GatewaysCollectionResponse, GeneralManagementApi } from '@vi-iot/client';

import { GatewaysActions } from './gateways.actions';
import { isNBIoTAndNotRegistered } from './gateways.helpers';

@Injectable()
export class GatewaysEffects {
  listGatewaysSearchResult$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewaysActions.listGatewaysSearchResult),
      switchMap((action) =>
        this.generalManagementApi
          .getGatewaysCollection({
            serial: action.gatewaySerial,
            limit: action.limit,
          })
          .pipe(
            map((response: GatewaysCollectionResponse) =>
              GatewaysActions.listGatewaysSearchResultSuccess({
                gateways: response.data,
                cursor: response.cursor.next,
              })
            ),
            catchError(() => of(GatewaysActions.listGatewaysSearchResultError()))
          )
      )
    );
  });

  listGatewaysSearchFetchMore$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewaysActions.listGatewaysSearchFetchMore),
      switchMap((action) =>
        this.generalManagementApi
          .getGatewaysCollection({
            serial: action.gatewaySerial,
            limit: action.limit,
            cursor: action.cursor,
          })
          .pipe(
            tap((response: GatewaysCollectionResponse) => {
              if (isEmpty(response)) {
                throw new Error('Data is empty');
              }
            }),
            map((response: GatewaysCollectionResponse) =>
              GatewaysActions.listGatewaysSearchFetchMoreSuccess({
                gateways: response.data,
                cursor: response.cursor.next,
              })
            ),
            catchError(() => of(GatewaysActions.listGatewaysSearchFetchMoreError()))
          )
      )
    );
  });

  fetchGatewayActiveErrors$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        GatewaysActions.listGatewaysSearchResultSuccess,
        GatewaysActions.listGatewaysSearchFetchMoreSuccess
      ),
      mergeMap(({ gateways }) =>
        forkJoin(
          gateways.filter(isNBIoTAndNotRegistered).reduce(
            (acc, gateway) => ({
              ...acc,
              [gateway.serial]: this.gatewayApi.getGatewaySmartNotes(gateway.serial).pipe(
                map((response) => response.data),
                catchError(() => of(null))
              ),
            }),
            {}
          )
        )
      ),
      map((activeErrors) => GatewaysActions.FetchGatewayActiveErrorsSuccess({ activeErrors }))
    );
  });

  constructor(
    private actions$: Actions,
    private generalManagementApi: GeneralManagementApi,
    private gatewayApi: GatewayApi
  ) {}
}
